import React, { Fragment, useEffect, useState } from 'react';
import Loader from '../../Loader';
import { Link } from 'react-router-dom';
import { useAlert } from 'react-alert';
import { useDispatch, useSelector } from 'react-redux';
import { getUniversityDetail, getUniversityList, updateUniversity } from '../../../actions/adminUniversityAction';
import { UPDATE_UNIVERSITY_RESET } from '../../../constants/adminUniversityConstants';
import { subjectLogoUrl } from '../../../utils/assets';

const resolveImagePreview = (value) => {
    if (!value) {
        return '';
    }

    if (typeof value === 'string' && /^data:/i.test(value)) {
        return value;
    }

    if (typeof value === 'string' && /^https?:\/\//i.test(value)) {
        return value;
    }

    return subjectLogoUrl(value);
};

const EditUniversity = ({ history, match }) => {
    const alert = useAlert();
    const dispatch = useDispatch();

    const [universityId, setUniversityId] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState('');
    const [logo, setLogo] = useState('');
    const [imageName, setImageName] = useState('');
    const [oldImage, setOldImage] = useState('');
    const [imagePreview, setImagePreview] = useState('');

    const { university, isUniversityUpdated, loading, error } = useSelector((state) => state.university);

    useEffect(() => {
        dispatch(getUniversityDetail(match.params.id));
    }, [dispatch, match.params.id]);

    useEffect(() => {
        if (university) {
            setUniversityId(university._id || '');
            setTitle(university.title || '');
            setDescription(university.description || '');
            setStatus(university.status ? 'Active' : 'InActive');
            setLogo('');
            setImageName('');
            setOldImage(university.logo || '');
            setImagePreview(resolveImagePreview(university.logo));
        }

        if (error) {
            alert.error(error);
        }

        if (isUniversityUpdated) {
            alert.success('University updated successfully');
            dispatch(getUniversityList());
            history.push('/admin/university');
            dispatch({
                type: UPDATE_UNIVERSITY_RESET,
            });
        }
    }, [university, error, isUniversityUpdated, alert, dispatch, history]);

    const onFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) {
            return;
        }

        if (!file.name.match(/\.(jpg|jpeg|png|gif)$/i)) {
            setLogo('');
            setImageName('');
            alert.error('Please select valid image.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const result = event.target?.result;
            if (!result || typeof result !== 'string') {
                return;
            }

            setLogo(result);
            setImageName(file.name);
            setImagePreview(result);
        };

        reader.readAsDataURL(file);
    };

    const submitHandler = (e) => {
        e.preventDefault();

        const isDataUrl = typeof logo === 'string' && logo.startsWith('data:');
        const payloadLogo = isDataUrl ? logo : '';
        const payloadImageName = isDataUrl ? imageName : '';
        const payloadOldImage = oldImage || '';

        if (!payloadLogo && !payloadOldImage) {
            alert.error('Select university image');
            return;
        }

        dispatch(
            updateUniversity(
                universityId,
                title,
                description,
                status,
                payloadLogo,
                payloadImageName,
                payloadOldImage
            )
        );
    };

    return (
        <Fragment>
            {loading ? (
                <Loader />
            ) : (
                <Fragment>
                    <div className="content-wrapper">
                        <section className="content-header">
                            <h1>
                                Edit
                                <small>University</small>
                            </h1>
                            <ol className="breadcrumb">
                                <li>
                                    <Link to="/admin">
                                        <i className="fa fa-dashboard" /> Dashboard
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/admin/university">
                                        <i className="fa fa-users" /> University
                                    </Link>
                                </li>
                                <li className="active">Edit University</li>
                            </ol>
                        </section>

                        <section className="content">
                            <div className="box box-default">
                                <div className="box-header with-border">
                                    <h3 className="box-title">Edit University</h3>
                                </div>

                                <form id="quickForm" onSubmit={submitHandler} encType="multipart/form-data">
                                    <div className="box-body">
                                        <div className="form-group row">
                                            <div className="col-md-6">
                                                <label htmlFor="title">Title</label>
                                                <input
                                                    type="text"
                                                    name="title"
                                                    className="form-control"
                                                    id="title"
                                                    placeholder="Enter Title"
                                                    value={title}
                                                    onChange={(e) => setTitle(e.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label htmlFor="description">Description</label>
                                                <input
                                                    type="text"
                                                    name="description"
                                                    className="form-control"
                                                    id="description"
                                                    placeholder="Enter Description"
                                                    value={description}
                                                    onChange={(e) => setDescription(e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group row">
                                            <div className="col-md-6">
                                                <label htmlFor="logo">Image</label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    id="logo"
                                                    name="logo"
                                                    onChange={onFileChange}
                                                    accept=".jpeg, .jpg, .png"
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label htmlFor="status">Status</label>
                                                <select
                                                    className="form-control"
                                                    name="status"
                                                    id="status"
                                                    value={status}
                                                    onChange={(e) => setStatus(e.target.value)}
                                                >
                                                    <option value="">Select Status</option>
                                                    <option value="Active">Active</option>
                                                    <option value="InActive">In Active</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="form-group row">
                                            <div className="col-md-6">
                                                <label htmlFor="preview">Preview</label>
                                                <div id="preview" className="exam-image-preview">
                                                    <img
                                                        id="img-preview"
                                                        src={imagePreview || subjectLogoUrl(oldImage)}
                                                        alt="University preview"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="box-footer">
                                        <button type="submit" className="btn btn-primary">
                                            Submit
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </section>
                    </div>
                </Fragment>
            )}
        </Fragment>
    );
};

export default EditUniversity;